import {
  loadDashboardHeatmap,
  renderDashboardAccessTokens,
  renderDashboardIssues,
  renderDashboardSkills,
  renderDashboardSubmissions,
} from '../lib/dashboard-activity';
import {
  changeAdminUsersPage,
  clearAdminUsersFilters,
  filterAdminUsers,
  loadAdminUsers,
  retryAdminUsersLoad,
  setCurrentAdminUser,
  setRole,
  toggleSuspend,
  usersSearch,
} from '../lib/dashboard-admin-users';
import { hasAdminUsersViewState } from '../lib/dashboard-admin-users-state';
import {
  buildAccessTokenCreationErrorMessage,
  buildAccessTokenRevokeErrorMessage,
  buildAccessTokenRevokeSuccessMessage,
  createAccessToken,
  filterJobs,
  loadDashboardData,
  loadDashboardOperatorData,
  revokeAccessToken,
} from '../lib/dashboard-operator';
import { renderDashboardNextStep } from '../lib/dashboard-work-queue-view';

export type { DashboardPageData } from '../lib/api-client';
export {
  buildAdminUserActionSummary,
  buildAdminUsersEmptyMessage,
  buildAdminUsersViewSearch,
  buildAdminUsersViewSummary,
  hasAdminUsersViewState,
  resolveAdminUsersLoadedPage,
  resolveAdminUsersViewState,
  type AdminUserActionSummary,
  type AdminUsersFilterKey,
  type AdminUsersLoadedPageInput,
  type AdminUsersViewState,
} from '../lib/dashboard-admin-users-state';
export {
  buildAccessTokenCreationErrorMessage,
  buildAccessTokenRevokeErrorMessage,
  buildAccessTokenRevokeSuccessMessage,
  createAccessToken,
  loadDashboardData,
  revokeAccessToken,
  type DashboardActionMessage,
} from '../lib/dashboard-operator';
export {
  selectDashboardNextStep,
  selectDashboardWorkQueue,
  type DashboardNextStep,
  type DashboardNextStepAccount,
  type DashboardNextStepInput,
  type DashboardNextStepRole,
  type DashboardWorkQueueCard,
} from '../lib/dashboard-work-queue';

if (typeof window !== 'undefined') {
  window.DashboardData = {
    loadDashboardData,
    createAccessToken,
    revokeAccessToken,
  };
}

export type DashboardSection = 'issues' | 'submissions' | 'skills' | 'access-tokens' | 'admin';

const DASHBOARD_SECTION_PARAM = 'section';

function normalizeDashboardSection(value: string | null): DashboardSection | null {
  if (
    value === 'issues'
    || value === 'submissions'
    || value === 'skills'
    || value === 'access-tokens'
    || value === 'admin'
  ) {
    return value;
  }
  return null;
}

export function resolveDashboardSection(search: string, hash = ''): DashboardSection | null {
  if (hash === '#access-tokens') return 'access-tokens';
  return normalizeDashboardSection(new URLSearchParams(search).get(DASHBOARD_SECTION_PARAM));
}

export function buildDashboardSectionSearch(search: string, section: DashboardSection): string {
  const params = new URLSearchParams(search);
  params.set(DASHBOARD_SECTION_PARAM, section);
  const next = params.toString();
  return next ? `?${next}` : '';
}

if (typeof window !== 'undefined') {
  window.DashboardNavigation = {
    resolveDashboardSection,
    buildDashboardSectionSearch,
  };
}

if (typeof window !== 'undefined') {
  window.DashboardHeatmap = { loadHeatmap: loadDashboardHeatmap };
}

if (typeof window !== 'undefined') {
  window.DashboardNextStep = { render: renderDashboardNextStep };
  window.DashboardRenderers = {
    buildAccessTokenCreationErrorMessage,
    buildAccessTokenRevokeErrorMessage,
    buildAccessTokenRevokeSuccessMessage,
    renderIssues: renderDashboardIssues,
    renderSubmissions: renderDashboardSubmissions,
    renderSkills: renderDashboardSkills,
    renderAccessTokens: renderDashboardAccessTokens,
  };
}

async function loadAdminData(): Promise<void> {
  await Promise.all([loadAdminUsers(), loadDashboardOperatorData()]);
}

if (typeof window !== 'undefined') {
  window.DashboardAdmin = {
    loadAdminData,
    filterJobs,
    setCurrentUser: setCurrentAdminUser,
    setRole,
    toggleSuspend,
    usersSearch,
    filterAdminUsers,
    clearAdminUsersFilters,
    changeAdminUsersPage,
    retryAdminUsersLoad,
    hasAdminUsersViewState,
  };
  window.filterJobs = filterJobs;
  window.adminSetRole = setRole;
  window.adminToggleSuspend = toggleSuspend;
  window.adminUsersSearch = usersSearch;
  window.adminUsersFilter = filterAdminUsers;
  window.adminUsersClear = clearAdminUsersFilters;
  window.adminUsersPageChange = changeAdminUsersPage;
  window.adminUsersRetry = retryAdminUsersLoad;
}
